import {
  Box,
  Button,
  Modal,
  Tooltip,
  IconButton,
  TextField,
  Typography,
  LinearProgress,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Chip,
  Avatar,
  FormHelperText,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { BUTTON_STYLES, MODAL_STYLES } from "../../styles/componentStyles";
import { useFormik } from "formik";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import BadgeIcon from "@mui/icons-material/Badge";
import SchoolIcon from "@mui/icons-material/School";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const PROFILE_IMAGE_URL = "/api/ftp/mentorProfileImageById";
const MENTOR_DOCUMENTS_URL = "/api/ftp/uploadMentorDocumentsById";
const DEMO_VIDEO_URL = "/api/ftp/uploadMentorDemoVideoById";
const DELETE_URL = "/api/ftp/delete";

const BRAND = "#17479e";

// Resolve a stored path/URL to an absolute media URL
const resolveMediaUrl = (path) =>
  !path
    ? ""
    : /^https?:\/\//.test(path)
      ? path
      : `https://siaedutech.com/public/${path}`;

const baseName = (path) => (path ? path.split("/").pop() : "");
const isImagePath = (path) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(path || "");

// A document is considered approved when the backend reports it APPROVED
// (the review API returns "APPROVED"); "VERIFIED" is accepted for older data
// — mirrors the same check in MentorsTable.jsx.
const isDocApproved = (status) =>
  status === "APPROVED" || status === "VERIFIED";

function ApprovalChip({ status }) {
  if (!status) return <Chip label="Pending Review" size="small" />;
  const approved = isDocApproved(status);
  return (
    <Chip
      icon={approved ? <CheckCircleIcon /> : undefined}
      label={approved ? "Approved" : "Rejected"}
      color={approved ? "success" : "error"}
      size="small"
      variant={approved ? "filled" : "outlined"}
    />
  );
}

const DOCS = [
  {
    key: "profileImage",
    label: "Profile Image",
    hint: "PNG or JPG",
    accept: "image/*",
    uploadUrl: PROFILE_IMAGE_URL,
    Icon: ImageIcon,
  },
  {
    key: "aadharCard",
    label: "ID Proof (PAN / Aadhar)",
    hint: "Image or PDF",
    accept: "image/*,.pdf",
    uploadUrl: MENTOR_DOCUMENTS_URL,
    docType: "AADHAAR",
    Icon: BadgeIcon,
  },
  {
    key: "highestQualification",
    label: "Highest Qualification",
    hint: "Image or PDF",
    accept: "image/*,.pdf",
    uploadUrl: MENTOR_DOCUMENTS_URL,
    docType: "QUALIFICATION",
    Icon: SchoolIcon,
  },
  {
    key: "experience",
    label: "Experience Proof",
    hint: "Image or PDF",
    accept: "image/*,.pdf",
    uploadUrl: MENTOR_DOCUMENTS_URL,
    docType: "EXPERIENCE_PROOF",
    Icon: WorkHistoryIcon,
  },
  {
    key: "bankDetails",
    label: "Bank Details",
    hint: "Image or PDF",
    accept: "image/*,.pdf",
    uploadUrl: MENTOR_DOCUMENTS_URL,
    docType: "BANK_DETAILS",
    Icon: AccountBalanceIcon,
  },
];

function DocUpload({
  label,
  hint,
  accept,
  fieldKey,
  formik,
  MentorId,
  uploadUrl,
  docType,
  Icon,
  existingDoc,
}) {
  const fileInputRef = useRef(null);
  const [uploadedPath, setUploadedPath] = useState(
    existingDoc?.filePath || formik.values[fieldKey] || "",
  );
  const [docStatus, setDocStatus] = useState(existingDoc?.status || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setError("");
    // Already has a file (approved or not) — picking a new one replaces it
    // immediately, since there's no separate "Upload" button in that view.
    if (uploadedPath) handleUpload(file);
  };

  const handleUpload = async (fileArg) => {
    const file = fileArg || selectedFile;
    if (!file) return;
    const previousPath = uploadedPath;
    setLoading(true);
    setProgress(0);
    setError("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post(uploadUrl, formData, {
        params: {
          userId: MentorId,
          ...(docType ? { docType } : {}),
        },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      const newPath = response.data?.data?.path;
      setUploadedPath(newPath);
      formik.setFieldValue(fieldKey, newPath);
      setDocStatus(null);
      setSelectedFile(null);
      setProgress(100);
      toast.success(`${label} ${previousPath ? "replaced" : "uploaded"}.`);
      if (previousPath && previousPath !== newPath) {
        // Best-effort cleanup of the old file — don't block on it.
        api.put(DELETE_URL, { path: previousPath }).catch(() => {});
      }
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.errorMessage ||
        "Upload failed. Please try again.";
      setError(message);
      toast.error(message);
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await api.put(DELETE_URL, { path: uploadedPath });
      setUploadedPath("");
      setDocStatus(null);
      setSelectedFile(null);
      setProgress(0);
      formik.setFieldValue(fieldKey, "");
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success(`${label} removed.`);
    } catch (err) {
      const message =
        err.response?.data?.errorMessage || "Failed to delete the file.";
      setError(message);
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const hasError = formik.touched[fieldKey] && Boolean(formik.errors[fieldKey]);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: hasError ? "error.main" : "divider",
        borderRadius: 2,
        overflow: "hidden",
        transition: "box-shadow .2s, border-color .2s",
        "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,.06)" },
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 1.25,
          bgcolor: "#f6f8fc",
        }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: "#e5edfb", color: BRAND, width: 36, height: 36 }}
        >
          <Icon fontSize="small" />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        </Box>
        {uploadedPath ? (
          docType ? (
            <ApprovalChip status={docStatus} />
          ) : (
            <Chip
              icon={<CheckCircleIcon />}
              label="Uploaded"
              color="success"
              size="small"
              variant="outlined"
            />
          )
        ) : (
          <Chip label="Pending" size="small" variant="outlined" />
        )}
      </Box>

      <Divider />

      {/* Body */}
      <Box sx={{ p: 1.5 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          hidden
          onChange={handleFileChange}
        />
        {uploadedPath ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1,
              borderRadius: 1.5,
              bgcolor: docType && !isDocApproved(docStatus) ? "#fdf6ec" : "#e9f7ec",
              border: `1px solid ${
                docType && !isDocApproved(docStatus) ? "#f0d9a6" : "#b6e0be"
              }`,
            }}
          >
            {isImagePath(uploadedPath) ? (
              <Avatar
                variant="rounded"
                src={resolveMediaUrl(uploadedPath)}
                sx={{ width: 44, height: 44 }}
              />
            ) : (
              <Avatar
                variant="rounded"
                sx={{ bgcolor: "#c8e6c9", color: "#2e7d32", width: 44, height: 44 }}
              >
                <DescriptionIcon />
              </Avatar>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color:
                    docType && !isDocApproved(docStatus) ? "#8a6d3b" : "#2e7d32",
                }}
              >
                {!docType
                  ? "File uploaded"
                  : docStatus
                    ? isDocApproved(docStatus)
                      ? "Approved"
                      : "Rejected"
                    : "Awaiting review"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: "block" }}
              >
                {baseName(uploadedPath)}
              </Typography>
            </Box>
            <Tooltip title="View" arrow placement="top">
              <IconButton
                size="small"
                sx={{ color: BRAND }}
                onClick={() =>
                  window.open(resolveMediaUrl(uploadedPath), "_blank")
                }
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Replace file" arrow placement="top">
              <span>
                <IconButton
                  size="small"
                  sx={{ color: BRAND }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                >
                  <UploadFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remove" arrow placement="top">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: "none" }}
              >
                Choose File
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ flex: 1, minWidth: 80 }}
              >
                {selectedFile ? selectedFile.name : "No file chosen"}
              </Typography>
              <Button
                type="button"
                variant="contained"
                size="small"
                onClick={() => handleUpload()}
                disabled={!selectedFile || loading}
                sx={{ textTransform: "none", bgcolor: BRAND }}
              >
                {loading ? "Uploading…" : "Upload"}
              </Button>
            </Box>

            {loading && (
              <Box sx={{ mt: 1.25 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Uploading…
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {progress}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            )}
          </>
        )}

        {error && (
          <Typography sx={{ color: "error.main", fontSize: 13, mt: 1 }}>
            {error}
          </Typography>
        )}
        {hasError && (
          <FormHelperText sx={{ color: "error.main" }}>
            {formik.errors[fieldKey]}
          </FormHelperText>
        )}
      </Box>
    </Box>
  );
}

// Small popup to record a single demo video: subject + title + upload with a
// live progress bar. "Add Demo" hands the completed demo back to the parent.
function DemoVideoDialog({ open, onClose, subjects, MentorId, onAdd, onWatch }) {
  const videoInputRef = useRef(null);
  const [subjectId, setSubjectId] = useState("");
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoPath, setVideoPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setSubjectId("");
    setTitle("");
    setSelectedFile(null);
    setVideoPath("");
    setUploading(false);
    setProgress(0);
    setDeleting(false);
    setError("");
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please choose a video file first.");
      return;
    }
    setUploading(true);
    setProgress(0);
    setError("");

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await api.post(DEMO_VIDEO_URL, formData, {
        params: { userId: MentorId },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      setVideoPath(response.data?.data?.path);
      setProgress(100);
      toast.success("Demo video uploaded.");
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.errorMessage ||
        "Upload failed. Please try again.";
      setError(message);
      toast.error(message);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteVideo = async () => {
    if (!videoPath) return;
    setDeleting(true);
    try {
      await api.put(DELETE_URL, { path: videoPath });
      setVideoPath("");
      setProgress(0);
      setSelectedFile(null);
      if (videoInputRef.current) videoInputRef.current.value = "";
      toast.success("Demo video removed.");
    } catch (err) {
      const message =
        err.response?.data?.errorMessage || "Failed to delete the video.";
      setError(message);
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const handleAdd = () => {
    if (!subjectId) return setError("Please select a subject.");
    if (!title.trim()) return setError("Please enter a demo title.");
    if (!videoPath) return setError("Please upload the demo video.");

    const subject = subjects.find(
      (s) => String(s.subjectId ?? s.id ?? s.subjectName) === String(subjectId),
    );
    onAdd({
      groupSubjectId:
        subject?.groupSubjectId ?? subject?.subjectId ?? subject?.id ?? subjectId,
      subjectId,
      subjectName: subject?.subjectName || "",
      title: title.trim(),
      videoPath,
    });
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Branded header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${BRAND}, #2a63c0)`,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <OndemandVideoIcon />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Add Demo Video
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="Subject"
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
          sx={{ mb: 2 }}
        >
          {subjects.length === 0 && (
            <MenuItem value="" disabled>
              No subjects available
            </MenuItem>
          )}
          {subjects.map((s) => {
            const value = String(s.subjectId ?? s.id ?? s.subjectName);
            return (
              <MenuItem key={value} value={value}>
                {s.subjectName}
              </MenuItem>
            );
          })}
        </TextField>

        <TextField
          fullWidth
          size="small"
          label="Demo Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Upload zone */}
        <Box
          sx={{
            border: "1px dashed",
            borderColor: videoPath ? "#b6e0be" : "#c3ccdb",
            bgcolor: videoPath ? "#e9f7ec" : "#f6f8fc",
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            hidden
            onChange={handleFileChange}
          />
          {!videoPath ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => videoInputRef.current?.click()}
                sx={{ textTransform: "none" }}
              >
                Choose Video
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ flex: 1, minWidth: 80 }}
              >
                {selectedFile ? selectedFile.name : "No video chosen"}
              </Typography>
              <Button
                type="button"
                variant="contained"
                size="small"
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                sx={{ textTransform: "none", bgcolor: BRAND }}
              >
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                variant="rounded"
                sx={{ bgcolor: "#c8e6c9", color: "#2e7d32", width: 40, height: 40 }}
              >
                <OndemandVideoIcon />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#2e7d32" }}
                >
                  Video uploaded
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: "block" }}
                >
                  {baseName(videoPath)}
                </Typography>
              </Box>
              <Tooltip title="Watch" arrow placement="top">
                <IconButton
                  size="small"
                  sx={{ color: BRAND }}
                  onClick={() => onWatch(videoPath)}
                >
                  <PlayCircleFilledIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete video" arrow placement="top">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleDeleteVideo}
                    disabled={deleting}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}

          {uploading && (
            <Box sx={{ mt: 1.25 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Uploading…
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}
        </Box>

        {error && (
          <Typography sx={{ color: "error.main", fontSize: 13, mt: 1.5 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} {...BUTTON_STYLES.secondary}>
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          disabled={!videoPath}
          {...BUTTON_STYLES.primary}
        >
          Add Demo
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MentorDocumentsUploads({
  States,
  open,
  onClose,
  mentorshipDetailsEdit,
  onSuccess,
}) {
  const MentorId = mentorshipDetailsEdit?.profile?.mentorId;
  const mentorName = mentorshipDetailsEdit?.profile?.username;
  const subjects = mentorshipDetailsEdit?.subjects || [];
  const isEdit =
    mentorshipDetailsEdit && Object.keys(mentorshipDetailsEdit).length > 0;

  // Already-uploaded documents from the mentor's existing profile, keyed by
  // docType, so each DocUpload can show its current file + approval status.
  const getExistingDoc = (docType) =>
    mentorshipDetailsEdit?.documents?.find((d) => d.docType === docType);

  // Demo videos: seeded from the mentor's existing profile, then any added
  // via the small popup in this session are appended (kept in local state).
  const [demoVideos, setDemoVideos] = useState([]);
  const [openDemo, setOpenDemo] = useState(false);
  const [watchUrl, setWatchUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    setDemoVideos(
      (mentorshipDetailsEdit?.demoVideos || []).map((v) => ({
        id: v.id,
        groupSubjectId: v.groupSubjectId,
        subjectName: v.subjectName,
        title: v.title || "",
        videoPath: v.videoUrl,
        status: v.status,
      })),
    );
  }, [open, mentorshipDetailsEdit]);

  const handleAddDemo = (demo) => {
    setDemoVideos((prev) => [...prev, demo]);
  };

  const handleDeleteDemo = async (demo) => {
    try {
      if (demo.videoPath) await api.put(DELETE_URL, { path: demo.videoPath });
      setDemoVideos((prev) =>
        prev.filter((d) => d.videoPath !== demo.videoPath),
      );
      toast.success("Demo video removed.");
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage || "Failed to delete the demo video.",
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      profileImage:
        mentorshipDetailsEdit?.profile?.profileImageUrl ||
        mentorshipDetailsEdit?.profile?.profileImage ||
        "",
      aadharCard: getExistingDoc("AADHAAR")?.filePath || "",
      highestQualification: getExistingDoc("QUALIFICATION")?.filePath || "",
      experience: getExistingDoc("EXPERIENCE_PROOF")?.filePath || "",
      bankDetails: getExistingDoc("BANK_DETAILS")?.filePath || "",
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        setSubmitting(true);
        const payload = {
          profileImageUrl: values.profileImage || "",
          idProofUrl: values.aadharCard || "",
          qualificationProofUrl: values.highestQualification || "",
          experienceProofUrl: values.experience || "",
          bankProofUrl: values.bankDetails || "",
          demoVideos: demoVideos.map((d) => ({
            groupSubjectId: Number(d.groupSubjectId) || d.groupSubjectId,
            title: d.title,
            videoUrl: d.videoPath,
          })),
        };
        const response = await api.post(
          "/mentors/documentsAndDemoVideosUploadById",
          payload,
          { params: { userId: MentorId } },
        );
        toast.success("Documents saved successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
        onSuccess?.(response.data?.data);
        resetForm();
        setDemoVideos([]);
        onClose();
      } catch (error) {
        toast.error(
          error.response?.data?.errorMessage ||
            "Failed to save. Please try again.",
          { autoClose: 3000 },
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
      <Modal open={open} onClose={onClose} disableScrollLock>
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            ...MODAL_STYLES.standard,
            width: { xs: "94%", sm: 480, md: 580 },
            maxWidth: "94vw",
            maxHeight: "92vh",
            p: 0,
            borderRadius: 3,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              background: `linear-gradient(135deg, ${BRAND}, #2a63c0)`,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                {isEdit ? "Update Documents" : "Upload Documents"}
              </Typography>
              {(mentorName || MentorId) && (
                <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
                  {mentorName}
                  {mentorName && MentorId ? " • " : ""}
                  {MentorId ? `ID ${MentorId}` : ""}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body (scrollable) */}
          <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.6 }}
            >
              Documents
            </Typography>
            <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
              {DOCS.map((doc) => (
                <DocUpload
                  key={doc.key}
                  label={doc.label}
                  hint={doc.hint}
                  accept={doc.accept}
                  fieldKey={doc.key}
                  formik={formik}
                  MentorId={MentorId}
                  uploadUrl={doc.uploadUrl}
                  docType={doc.docType}
                  Icon={doc.Icon}
                  existingDoc={doc.docType ? getExistingDoc(doc.docType) : null}
                />
              ))}
            </Stack>

            {/* Demo Videos */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 0.6 }}
              >
                Demo Videos
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setOpenDemo(true)}
                sx={{ textTransform: "none" }}
              >
                Add Demo
              </Button>
            </Box>

            {demoVideos.length === 0 ? (
              <Box
                sx={{
                  border: "1px dashed #c3ccdb",
                  borderRadius: 2,
                  py: 3,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <OndemandVideoIcon sx={{ fontSize: 30, opacity: 0.4 }} />
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  No demo videos added yet
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {demoVideos.map((demo) => (
                  <Box
                    key={demo.id ?? demo.videoPath}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      transition: "box-shadow .2s",
                      "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,.06)" },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{ bgcolor: "#e5edfb", color: BRAND, width: 40, height: 40 }}
                    >
                      <OndemandVideoIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        noWrap
                      >
                        {demo.subjectName || "—"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: "block" }}
                      >
                        {demo.title}
                      </Typography>
                    </Box>
                    <ApprovalChip status={demo.status} />
                    <Tooltip title="Watch" arrow placement="top">
                      <IconButton
                        size="small"
                        sx={{ color: BRAND }}
                        onClick={() => setWatchUrl(demo.videoPath)}
                      >
                        <PlayCircleFilledIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow placement="top">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDemo(demo)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "#fafbfd",
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
            }}
          >
            <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={formik.isSubmitting}
              {...BUTTON_STYLES.primary}
            >
              {formik.isSubmitting ? "Processing…" : "Save"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Add Demo Video popup */}
      <DemoVideoDialog
        open={openDemo}
        onClose={() => setOpenDemo(false)}
        subjects={subjects}
        MentorId={MentorId}
        onAdd={handleAddDemo}
        onWatch={(path) => setWatchUrl(path)}
      />

      {/* Watch demo video */}
      <Dialog
        open={!!watchUrl}
        onClose={() => setWatchUrl("")}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            py: 1.5,
            background: `linear-gradient(135deg, ${BRAND}, #2a63c0)`,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PlayCircleFilledIcon />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Demo Video
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setWatchUrl("")}
            sx={{ color: "#fff" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, bgcolor: "#000" }}>
          {watchUrl && (
            <video
              src={resolveMediaUrl(watchUrl)}
              controls
              autoPlay
              style={{
                width: "100%",
                maxHeight: "70vh",
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MentorDocumentsUploads;
